import  { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {  useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaTrash, FaPlus, FaMinus, FaArrowRight } from 'react-icons/fa';
import api from '../../axios/axios';
import Swal from 'sweetalert2';
import { setCartLength } from '../../redux/features/auth/userSlice';

const CartDetails = ({ fetchCart }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currentUser);
  const [totalPrice, setTotalPrice] = useState(0);
  const navigate = useNavigate();
  const cartDetails = useSelector((state) => state.user.cart);

  useEffect(() => {
    dispatch(setCartLength(0));
  }, [dispatch]);

  useEffect(() => {
    const updatedPrice = cartDetails.reduce((total, item) => {
      return total + (parseFloat(item.finalPrice) || 0);
    }, 0);
    setTotalPrice(updatedPrice);
  }, [cartDetails]);

  const updateQuantity = async (id, type) => {
    if (type === "increment") {
      try {
        const response = await api.put(`user/incrementQuantity/${id}`, { userId: currentUser._id });
        fetchCart();
      } catch (error) {
        Swal.fire("Something went wrong");
      }
    } else {
      try {
        const response = await api.put(`user/decrementQuantity/${id}`, { userId: currentUser._id });
        fetchCart();
      } catch (error) {
        Swal.fire("Something went wrong");
      }
    }
  };

  const removeItem = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "This item will be removed from your cart.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, remove it!',
      });

      if (result.isConfirmed) {
        await api.delete(`user/deletecartItem/${id}?userId=${currentUser._id}`);
        Swal.fire('Removed!', 'The item has been removed from your cart.', 'success');
        fetchCart();
      }
    } catch (error) {
      console.error('Error:', error);
      Swal.fire('Error!', 'Failed to remove the item. Please try again.', 'error');
    }
  };

  const handleCheckout = async () => {
    navigate('/checkout');
  };

//   if (loading) {
//     return <Spinner />;
//   }

  return (
    <div className="min-h-screen bg-gray-50 py-28 px-4 sm:px-6 lg:px-8">
      {/* Empty Cart State */}
      {cartDetails?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="relative w-40 h-40 mb-6">
            <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-75"></div>
            <div className="relative flex items-center justify-center w-full h-full">
              <FaShoppingCart className="text-blue-500 text-6xl" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-4 text-center">
            Your Cart is Empty
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-md text-center">
            Looks like you haven't added anything to your cart yet.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg 
            font-medium hover:from-blue-700 hover:to-blue-600 transition-all duration-300 
            flex items-center shadow-md hover:shadow-lg"
          >
            Continue Shopping
            <FaArrowRight className="ml-2" />
          </button>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
            <FaShoppingCart className="mr-3 text-blue-500" />
            Your Shopping Cart
            <span className="ml-3 bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
              {cartDetails?.length} {cartDetails?.length === 1 ? 'item' : 'items'}
            </span>
          </h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cartDetails?.map((item) => (
                <div key={item._id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                  <div className="flex flex-col sm:flex-row">
                    {/* Product Image */}
                    <div className="sm:w-1/3 p-4">
                      <div className="aspect-w-1 aspect-h-1 bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={item.attributes?.find(attr => attr.attributeType === 'color')?.image || "https://via.placeholder.com/300"}
                          alt={item.productDetails?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    {/* Product Details */}
                    <div className="sm:w-2/3 p-4 flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between items-start mb-2">
                          <h2 className="text-xl font-bold text-gray-800">
                            {item.productDetails?.name || "Product Name"}
                          </h2>
                          <button
                            onClick={() => removeItem(item._id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            aria-label="Remove item"
                          >
                            <FaTrash />
                          </button>
                        </div>

                        {/* Attributes */}
                        <div className="mb-4">
                          {item.attributes?.map((attribute, index) => (
                            <div key={index} className="flex items-center text-sm text-gray-600 mb-1">
                              <span className="font-medium text-gray-700 w-20">{attribute.attributeType}:</span>
                              <span>{attribute.attributeName}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Price and Quantity */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="text-xl font-bold text-blue-600">
                          Base Price:${item?.productDetails?.price || "0.00"}
                        </div>

                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateQuantity(item._id, 'decrement')}
                            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                            disabled={item.quantity <= 1}
                          >
                            <FaMinus className={`${item.quantity <= 1 ? 'text-gray-400' : 'text-gray-600'}`} />
                          </button>
                          <span className="font-semibold w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item._id, 'increment')}
                            className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                          >
                            <FaPlus className="text-gray-600" />
                          </button>
                        </div>

                        <div className="text-lg font-bold text-right">
                          ${(item.productDetails?.price * item.quantity)?.toFixed(2) || "0.00"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Continue Shopping Button */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                  <FaArrowRight className="transform rotate-180 mr-2" />
                  Continue Shopping
                </button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:sticky lg:top-8 h-fit">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  Order Summary
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">${totalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-medium">Calculated at checkout</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax</span>
                    <span className="font-medium">Calculated at checkout</span>
                  </div>
                  <hr className="border-gray-200 my-2" />
                  <div className="flex justify-between text-xl font-bold text-gray-900">
                    <span>Estimated Total</span>
                    <span>${totalPrice.toFixed(2)}</span>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white 
                  font-bold hover:from-green-600 hover:to-green-700 transition-all duration-300 
                  flex items-center justify-center shadow-md hover:shadow-lg"
                >
                  Proceed to Checkout
                  <FaArrowRight className="ml-3" />
                </button>

                <div className="mt-6 text-sm text-gray-500">
                  <p className="flex items-center mb-2">
                    <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Free returns within 30 days
                  </p>
                  <p className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Secure checkout
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartDetails;